import type { IEventChannel } from "../model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";

const $: JQueryStatic = global.$;
const es = global.es;

/**
 * @class WebSocketModule
 */
export default class WebSocketModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;

    private ws: WebSocket|null = null;
    private wsEventChannel: IEventChannel | null = null;
    private dfd: JQueryDeferred<WebSocket> | null = null;
    private webSocketErrorCount: number;

    /**
     * 
     * @param eventChannel 
     * @param rqBroker 
     * @param resourceName 
     * @param cache 
     */
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;

        // TODO FIXME Is this required? Does setting to null even make sense nowadays?
        eventChannel.addListener("onDestroy", () => {
            this.dfd?.done(() => {
                this.ws?.close();
                this.wsEventChannel?.destroy();

                // prevent memory leaks when the map widget is destroyed
                this.eventChannel = this.rqBroker = this.resourceName = this.ws = this.wsEventChannel = this.dfd = this.webSocketErrorCount = null;
            });
        });

        this.createWebSocketIfRequired = this.createWebSocketIfRequired.bind(this);
        this.createWebSocket = this.createWebSocket.bind(this);
        this.onWebSocketMessage = this.onWebSocketMessage.bind(this);
        this.onWebSocketClose = this.onWebSocketClose.bind(this);
        this.onWebSocketError = this.onWebSocketError.bind(this);
        this.send = this.send.bind(this);
        this.addListener = this.addListener.bind(this);
        this.removeListener = this.removeListener.bind(this);
        this.addOpenListener = this.addOpenListener.bind(this);
        this.removeOpenListener = this.removeOpenListener.bind(this);
    }
    
    /**
     * 
     * @returns 
     */
    private createWebSocketIfRequired() {
        if (this.dfd) {
            return this.dfd.promise();
        }

        this.dfd = $.Deferred();
        if (typeof WebSocket == 'undefined') {
            return this.dfd.reject().promise();
        }

        this.wsEventChannel = es.util.createEventChannel({'onWebSocketMessage': 1, 'onWebSocketOpen': 1}, "es.module.WebSocket");
        this.createWebSocket(this.dfd);
        
        return this.dfd.promise();
    }

    /**
     * 
     * @param dfd 
     */
    private createWebSocket(dfd) {
        let endPointUrl = es.appUrl.endsWith('/') ? es.appUrl : es.appUrl + '/';
        if (endPointUrl.indexOf("/") === 0) { // relative URL
            endPointUrl = location.protocol + "//" + location.host + endPointUrl;
        }
        endPointUrl = endPointUrl.replace('http', 'ws') + 'websocket';

        this.ws = new WebSocket(endPointUrl);
        this.ws.onopen = () => {
            this.webSocketErrorCount = 0;
            dfd.resolve(this.ws);
            this.wsEventChannel?.fireEvent('onWebSocketOpen');
        };
        this.ws.onmessage = this.onWebSocketMessage;
        this.ws.onclose = this.onWebSocketClose;
        this.ws.onerror = this.onWebSocketError;
    }

    /**
     * 
     * @param event 
     */
    private onWebSocketMessage(event: MessageEvent) {
        var rs = JSON.parse(event.data); // is it always json?!
        this.wsEventChannel?.fireEvent('onWebSocketMessage', rs);
    }
    
    /**
     * 
     * @param e 
     * 
     * @returns 
     */
    private onWebSocketClose(e: CloseEvent) {
        if (this.ws == null) {
            es.log.info('[onWebSocketClose] it was destroyed, good that it is closed :)');
            return;
        }
        
        var timeout;
        if (this.dfd.state() == 'pending') {
            es.log.warn('[onWebSocketClose] Reconnect failed!!! Retrying...', e);
            timeout = 1500;
        } else {
            es.log.warn('[onWebSocketClose] Got closed!!!!!!! Reconnecting...', e);
            timeout = 500;
        }
        
        this.dfd = $.Deferred();
        setTimeout(() => {
            this.createWebSocket(this.dfd);
        }, timeout);
    }
    
    /**
     * 
     * @param e 
     */
    private onWebSocketError(e: Event) {
        this.webSocketErrorCount++;
        console.error('[onWebSocketError] Error, closing websocket (count: ' + this.webSocketErrorCount + ')...', e);
        this.ws.close();
        if (this.webSocketErrorCount > 10) {
            $.ajax({url: es.appUrl + 'sessionready', dataType: 'json'}).done(() => {
                this.webSocketErrorCount = 0;
            }).fail(function (jqXHR, statusText, errorThrown) {
                // In safari it seems that when websocket connection fails, it causes this to be thrown
                // making us believe that connection timed out.
                if (!(jqXHR.readyState == 0 && jqXHR.status == 0 && statusText == 'error')) {
                    es.api.getEventChannel().fireEvent("onSessionTimeout", "websocket_err_max_session_ready_fail");
                }
            });
        }
    }
    
    /**
     * Sends a message to the server through the web socket
     * 
     * @param message the message to be sent (JS Object)
     */
    send(message: object) {
        this.createWebSocketIfRequired().done((ws) => {
            ws.send(JSON.stringify(message));
        });
    }
    
    /**
     * Adds a listener for server side updates
     * 
     * @param callback function invoked when a server side update is received
     * @param owner 'this' in the callback function
     */
    addListener(callback: Function, owner) {
        this.createWebSocketIfRequired().done(() => {
            this.wsEventChannel.addListener('onWebSocketMessage', callback, owner);
        });
    }
    
    /**
     * Removes a listener for server side updates
     *
     * @param callback function invoked when a server side update is received
     * @param owner 'this' in the callback function
     */
    removeListener(callback: Function, owner) {
        this.dfd?.done(() => {
            this.wsEventChannel.removeListener('onWebSocketMessage', callback, owner);
            if (this.wsEventChannel.getListenersSize() == 0) {
                this.ws.close();
                this.wsEventChannel.destroy();

                this.ws = this.wsEventChannel = this.dfd = this.webSocketErrorCount = null;
            }
        });
    }

    /**
     * Registers a callback that fires every time the WebSocket connects, including reconnects.
     * Use this to (re-)send server-side subscription messages (e.g. ListenBrowserToolCalls)
     * that would otherwise only be sent once and lost when the socket reconnects.
     *
     * The callback fires once for the current connection (immediately if already open) and
     * again on every subsequent reopen.
     *
     * @param callback function invoked on each WS open
     * @param owner 'this' in the callback function
     */
    addOpenListener(callback: Function, owner?) {
        this.createWebSocketIfRequired().done(() => {
            // ensure callback runs for the connection that's open right now
            callback.call(owner);
        });
        this.wsEventChannel.addListener('onWebSocketOpen', callback, owner);
    }

    /**
     * @param callback the same function passed to addOpenListener
     * @param owner the same 'this' context passed to addOpenListener
     */
    removeOpenListener(callback: Function, owner?) {
        this.dfd?.done(() => {
            this.wsEventChannel.removeListener('onWebSocketOpen', callback, owner);
        });
    }
}
